import React, { useEffect, useMemo, useState } from 'react';

import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOff } from '@fortawesome/pro-duotone-svg-icons';
import { faToggleOn } from '@fortawesome/pro-regular-svg-icons';

import { FlexBox } from '@eltoro-ui/components';
import { Loading } from 'Components';

import { getNotificationSettings, pushNotificationSetting, subscribeTopic } from 'Requests';

import { NotificationSettingType, UserDeviceEnum } from 'enums';
import type { NotificationSetting } from 'types';

import './NotificationSetting.scss';

const NotificationSettings = () => {
  const NotificationSettingsTitles = {
    [NotificationSettingType.ACCOUNT]: 'Account Notifications',
    [NotificationSettingType.CAMPAIGN]: 'Campaign Notifications',
    [NotificationSettingType.CONTACTS]: 'Contact Notifications',
    [NotificationSettingType.ASSISTANT_ALERTS]: 'Assistant Alerts Notifications',
  };

  const [notificationSetting, setNotificationSetting] = useState<NotificationSetting[]>([]);

  useEffect(() => {
    getNotificationSettings().then(res => {
      if (res.data) {
        if (res.data.notifications.length > 0) {
          setNotificationSetting(res.data.notifications);
        }
      }
    });
  }, []);

  const notificationTypes: { label: string; key: keyof NotificationSetting }[] = [
    { label: 'Push', key: 'status' },
    { label: 'SMS', key: 'sms_status' },
    { label: 'Email', key: 'email_status' },
  ];

  async function toggleTurnOffOnAllNotification(isAllOn: boolean) {
    const newValue = +!isAllOn;

    const updatedSettings = notificationSetting.map(setting => ({
      ...setting,
      status: newValue,
      sms_status: newValue,
      email_status: newValue,
    }));

    setNotificationSetting(updatedSettings);

    try {
      const settings = updatedSettings.map(({ type, status, sms_status, email_status }) => ({
        type,
        status,
        sms_status,
        email_status,
      }));

      await pushNotificationSetting({
        settings,
        device: UserDeviceEnum.WEB,
      });

      toast.success('Saved Successfully!');

      const user = localStorage.getItem('firebase_notification');
      if (user) {
        await subscribeTopic(user, newValue);
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        setNotificationSetting(notificationSetting);
      }
    }
  }

  const callNotificationToggle =
    (setting: NotificationSetting, field: keyof NotificationSetting, value: number) => async () => {
      const updatedSettings = notificationSetting.map(s => {
        if (s.type === setting.type) {
          return { ...s, [field]: value };
        }
        return s;
      });

      setNotificationSetting(updatedSettings);

      try {
        const settings = updatedSettings.map(({ type, status, sms_status, email_status }) => ({
          type,
          status,
          sms_status,
          email_status,
        }));

        await pushNotificationSetting({
          settings,
          device: UserDeviceEnum.WEB,
        });

        toast.success('Saved Successfully!');

        const user = localStorage.getItem('firebase_notification');
        if (user && field === 'status') {
          await subscribeTopic(user, +settings.some(s => s.status));
        }
      } catch (e) {
        if (e instanceof AxiosError) {
          setNotificationSetting(notificationSetting);
        }
      }
    };

  const isAllOn = useMemo(() => {
    return notificationSetting.every(
      ({ status, sms_status, email_status }) =>
        status === 1 && sms_status === 1 && email_status === 1
    );
  }, [notificationSetting]);

  return (
    <div className="notificationSetting">
      {notificationSetting.length ? (
        <div className="notificationSetting__container">
          <div className="notificationSetting__containerHeadingItem">
            <FlexBox
              alignItems="center"
              justifyContent="center"
              UNSAFE_className="notificationSetting__containerItemIconContainer"
              gap="90px"
            >
              {notificationTypes.map(notifType => (
                <span key={notifType.key}>{notifType.label}</span>
              ))}
            </FlexBox>
          </div>

          {notificationSetting.map(setting => (
            <div key={setting.type} className="notificationSetting__containerItem">
              <span className="notificationSetting__containerItemContent">
                {NotificationSettingsTitles[setting.type]}
              </span>
              <FlexBox
                alignItems="center"
                justifyContent="center"
                UNSAFE_className="notificationSetting__containerItemIconContainer"
                gap="90px"
              >
                {notificationTypes.map(({ label, key }) => (
                  <FontAwesomeIcon
                    key={key}
                    className="notificationSetting__containerItemIcon"
                    size="1x"
                    color={setting[key] === 1 ? '#FFB708' : 'lightgray'}
                    icon={setting[key] === 1 ? faToggleOn : faToggleOff}
                    onClick={callNotificationToggle(setting, key, +!setting[key])}
                  />
                ))}
              </FlexBox>
            </div>
          ))}

          <div className="notificationSetting__containerBottom">
            <button
              className="notificationSetting__button"
              type="button"
              onClick={() => toggleTurnOffOnAllNotification(isAllOn)}
            >
              Turn {isAllOn ? 'OFF' : 'ON'} all notifications
            </button>
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </div>
  );
};

export default NotificationSettings;
